import { useState } from 'react';
import type { ReactNode } from 'react';

import { useIntl } from 'react-intl';

import { Button, Col, Row, Space, Spin, Typography } from 'antd';

import MemberAvatar from '@src/components/MemberList/MemberAvatar';
import {
  JOURNEY_ASSIGNED_USERS_TYPE,
  type JourneyAssignedUsersType,
} from '@src/packages/workJourney/constants';
import { useWorkJourneyContext } from '@src/packages/workJourney/contexts';
import type {
  WorkJourneyAssignedUser,
  WorkJourneyUserSummary,
} from '@src/packages/workJourney/types';
import IntlMessages from '@src/util/IntlMessages';

import { InfoCardList } from '@src/components/InfoCard';

import styles from '../styles.module.less';
import UsersSelectionDrawer from './UsersSelectionDrawer';

const { Text } = Typography;

interface JourneyAssignedUsersProps {
  users: WorkJourneyAssignedUser[];
  removeUser: (user: WorkJourneyAssignedUser) => void;
  addUsers: (users: WorkJourneyAssignedUser[]) => void;
  type?: JourneyAssignedUsersType;
}

/**
 * @description  Component to show assigned users to a work journey
 */
const JourneyAssignedUsers = ({
  users,
  removeUser,
  addUsers,
  type = JOURNEY_ASSIGNED_USERS_TYPE.MEMBER,
}: JourneyAssignedUsersProps) => {
  const [isDrawerVisible, setIsDrawerVisible] = useState(false);
  const { isSaving } = useWorkJourneyContext();
  const intl = useIntl();
  const handleToggleDrawer = (value: boolean) => setIsDrawerVisible(!value);

  const handleConfirmSelection = (selectedUsers: WorkJourneyAssignedUser[]) => {
    const filteredUsers = selectedUsers.filter(user => {
      if (!users.find(existingUser => existingUser?.user?.id === user.id)) return true;
      return false;
    });

    const responseUsers = filteredUsers.map(user => ({ ...user, divisions: [] }));
    addUsers(responseUsers);
    setIsDrawerVisible(false);
  };

  const getName = (user: WorkJourneyUserSummary) => {
    if (user.firstName && user.lastName) return `${user?.firstName} ${user?.lastName}`;
    return user.displayName;
  };

  return (
    <div>
      {isSaving ? (
        <div className={styles.loadingContainer}>
          <Spin />
        </div>
      ) : (
        <Row className={styles.formSectionRow}>
          <Col xs={24} sm={24} md={24} lg={24} xl={24}>
            {users &&
              users.map(user => {
                const userToShow = user.user || user;
                return (
                  <InfoCardList
                    key={userToShow.id || `${userToShow.email}  `}
                    title={
                      <Space>
                        <Text>{getName(userToShow as WorkJourneyUserSummary)}</Text>
                      </Space>
                    }
                    avatar={<MemberAvatar user={userToShow} size={36} />}
                    buttonItems={[
                      {
                        type: 'light',
                        iconName: (
                          <span
                            className={`${styles.deleteIcon} material-symbols-outlined gx-mr-3`}
                          >
                            delete
                          </span>
                        ),
                        action: () => removeUser(user),
                      },
                    ]}
                  />
                );
              })}

            <Button
              type="primary"
              onClick={() => handleToggleDrawer(isDrawerVisible)}
              style={{ marginTop: 16, width: '100%' }}
            >
              <IntlMessages id="workJourney.add" />
            </Button>
          </Col>
        </Row>
      )}

      {/* Drawer to select users */}
      <UsersSelectionDrawer
        isVisible={isDrawerVisible}
        onCancel={() => handleToggleDrawer(isDrawerVisible)}
        onConfirm={handleConfirmSelection}
        selectedUsers={users}
        type={type}
        title={
          (type === JOURNEY_ASSIGNED_USERS_TYPE.MEMBER
            ? intl.formatMessage({ id: 'general.users.assigned' })
            : intl.formatMessage({ id: 'workJourney.assignedSupervisors' })) as ReactNode
        }
      />
    </div>
  );
};

export default JourneyAssignedUsers;
